import {DEFAULT_ANIMATION, TAnimation, createAnimation} from './animation';
import {DEFAULT_BORDER_RADIUS, createBorderRadius} from './border';
import {TBoxShadow, TBoxShadowOptions, createBoxShadow} from './boxShadow';
import {TControlColors, createControlColors} from './controlColors';
import {DEFAULT_PALETTE} from './defaultPalette';
import {TMergeValues, TPaletteColor} from './definitions';
import {DEFAULT_DIMENSIONS, TDimensions, createDimensions} from './dimensions';
import {COLORS} from './oldPalette';
import {DEFAULT_BRANDING, TBrandingPalette, createBrandingPalette, createPalette} from './palette';
import {DEFAULT_SPACING, createSpacing} from './spacer';
import {TTypography, createTypography} from './typography';
import {DEFAULT_Z_INDEX, TZIndex, createZIndex} from './zIndex';

export type TThemeOptions<
  TCustomPalette extends Readonly<Record<string, TPaletteColor>> = TEmptyObject,
  TCustomDimensions extends TDimensions = TEmptyObject,
  TCustomAnimation extends TAnimation = typeof DEFAULT_ANIMATION,
  TCustomZIndex extends TZIndex = TEmptyObject,
> = {
  /**
   * @deprecated Use palette and branding instead
   */
  color?: Readonly<TAnyObject>;
  palette?: TCustomPalette;
  branding?: TBrandingPalette<TCustomPalette>;
  animation?: TCustomAnimation;
  dimensions?: TCustomDimensions;
  boxShadow?: TBoxShadowOptions;
  zIndex?: TCustomZIndex;
  controlColors?: Partial<TControlColors>;
  typography?: TTypography;
};

export type TTheme<
  TCustomPalette extends Readonly<Record<string, TPaletteColor>> = TEmptyObject,
  TCustomDimensions extends Readonly<Record<string, number>> = TEmptyObject,
  TCustomAnimation extends TAnimation = typeof DEFAULT_ANIMATION,
  TCustomZIndex extends TZIndex = TEmptyObject,
> = {
  theme: {
    /**
     * @deprecated Use palette and branding instead
     */
    color: Readonly<TAnyObject>;
    palette: [keyof TCustomPalette] extends [never]
      ? typeof DEFAULT_PALETTE
      : TMergeValues<typeof DEFAULT_PALETTE, TCustomPalette>;
    branding: [keyof TCustomPalette] extends [never]
      ? typeof DEFAULT_BRANDING
      : TBrandingPalette<TMergeValues<typeof DEFAULT_PALETTE, TCustomPalette>>;
    dimensions: [keyof TCustomDimensions] extends [never]
      ? typeof DEFAULT_DIMENSIONS
      : TMergeValues<typeof DEFAULT_DIMENSIONS, TCustomDimensions>;
    animation: TCustomAnimation;
    spacing: typeof DEFAULT_SPACING;
    borderRadius: typeof DEFAULT_BORDER_RADIUS;
    boxShadow: TBoxShadow;
    zIndex: [keyof TCustomZIndex] extends [never]
      ? typeof DEFAULT_Z_INDEX
      : TMergeValues<typeof DEFAULT_Z_INDEX, TCustomZIndex>;
    controlColors: TControlColors;
    typography: TTypography;
  };
};

export const getTheme = <
  TCustomPalette extends Readonly<Record<string, TPaletteColor>> = TEmptyObject,
  TCustomDimensions extends TDimensions = TEmptyObject,
  TCustomAnimation extends TAnimation = typeof DEFAULT_ANIMATION,
  TCustomZIndex extends TZIndex = TEmptyObject,
>({
  animation: customAnimation,
  dimensions: customDimensions,
  color,
  palette: customPalette,
  branding: customBranding,
  boxShadow,
  controlColors,
  typography: customTypography,
  zIndex: customZIndex,
}: TThemeOptions<TCustomPalette, TCustomDimensions, TCustomAnimation, TCustomZIndex> = {}) => {
  const colorPalette = {...COLORS, ...color};
  const palette = createPalette(customPalette);

  return {
    animation: createAnimation(customAnimation),
    borderRadius: createBorderRadius(),
    boxShadow: createBoxShadow(boxShadow),
    branding: createBrandingPalette(customBranding),
    controlColors: createControlColors(controlColors),
    dimensions: createDimensions(customDimensions),
    spacing: createSpacing(),
    typography: createTypography(customTypography),
    zIndex: createZIndex(customZIndex),
    palette,
    color: colorPalette,
  } as const;
};
